import axios from 'axios';
import moment from 'moment';
import { put, all, takeLatest } from 'redux-saga/effects';
import { STATISTICS } from 'app/api-routes';
import { ALL, DATE_TIME_FORMAT } from 'app/types/constants';
import {
  biTasksByDateFailure,
  biTasksByDateRequest,
  biTasksByDateSuccess,
  biTasksBySiteAndDateFailure,
  biTasksBySiteAndDateRequest,
  biTasksBySiteAndDateSuccess,
  biTasksBySiteFailure,
  biTasksBySiteRequest,
  biTasksBySiteSuccess,
  biTasksScheduleStatisticsFailure,
  biTasksScheduleStatisticsRequest,
  biTasksScheduleStatisticsSuccess,
  biTasksByEmployeeFailure,
  biTasksByEmployeeRequest,
  biTasksByEmployeeSuccess,
  biTaskLogFailure,
  biTaskLogRequest,
  biTaskLogSuccess,
  setLoadingState,
} from './actions';

function* biTasksBySiteAndDateSaga({
  payload: { month, year, siteID, unitType, supervisorID, mobilePatrolID, clientID, statuses },
}) {
  const startDate = moment(`${year}-${month}`).startOf('month').format(DATE_TIME_FORMAT);
  const endDate = moment(`${year}-${month}`).endOf('month').format(DATE_TIME_FORMAT);

  const params = { startDate, endDate, timeZoneOffset: moment().utcOffset(), unitType };

  if (siteID) {
    params.siteID = siteID;
  }

  if (supervisorID) {
    params.supervisorID = supervisorID;
  }

  if (mobilePatrolID) {
    params.mobilePatrolID = mobilePatrolID;
  }

  if (clientID) {
    params.clientID = clientID;
  }

  if (statuses && statuses.length && !statuses.includes(ALL)) {
    params.statuses = statuses;
  }

  try {
    yield put(biTasksBySiteAndDateSuccess([]));
    yield put(setLoadingState(true));

    const { payload: responsePayload, warnings } = yield axios.get(`${STATISTICS}/tasks/group-by-site-and-date`, {
      params,
    });
    yield put(biTasksBySiteAndDateSuccess(responsePayload, { warnings }));
  } catch (err) {
    yield put(biTasksBySiteAndDateFailure(err));
  } finally {
    yield put(setLoadingState(false));
  }
}

function* biTasksByDateSaga({ payload: { month, year, siteID, unitType, supervisorID, mobilePatrolID, clientID } }) {
  const startDate = moment(`${year}-${month}`).startOf('month').format(DATE_TIME_FORMAT);
  const endDate = moment(`${year}-${month}`).endOf('month').format(DATE_TIME_FORMAT);

  const params = {
    startDate,
    endDate,
    timeZoneOffset: moment().utcOffset(),
    unitType,
  };

  if (siteID) {
    params.siteID = siteID;
  }

  if (supervisorID) {
    params.supervisorID = supervisorID;
  }

  if (mobilePatrolID) {
    params.mobilePatrolID = mobilePatrolID;
  }

  if (clientID) {
    params.clientID = clientID;
  }

  try {
    const { payload: responsePayload, warnings } = yield axios.get(`${STATISTICS}/tasks/group-by-date`, {
      params,
    });
    yield put(biTasksByDateSuccess(responsePayload, { warnings }));
  } catch (err) {
    yield put(biTasksByDateFailure(err));
  }
}

function* biTasksBySiteSaga({ payload: { month, year, siteID, unitType, supervisorID, mobilePatrolID, clientID } }) {
  const startDate = moment(`${year}-${month}`).startOf('month').format(DATE_TIME_FORMAT);
  const endDate = moment(`${year}-${month}`).endOf('month').format(DATE_TIME_FORMAT);

  const params = { startDate, endDate, timeZoneOffset: moment().utcOffset(), unitType };

  if (siteID) {
    params.siteID = siteID;
  }

  if (supervisorID) {
    params.supervisorID = supervisorID;
  }

  if (mobilePatrolID) {
    params.mobilePatrolID = mobilePatrolID;
  }

  if (clientID) {
    params.clientID = clientID;
  }

  try {
    const { payload: responsePayload, warnings } = yield axios.get(`${STATISTICS}/tasks/group-by-site`, {
      params,
    });
    yield put(biTasksBySiteSuccess(responsePayload, { warnings }));
  } catch (err) {
    yield put(biTasksBySiteFailure(err));
  }
}

function* biTasksScheduleStatisticsSaga({ payload: { month, year, siteID } }) {
  const startDate = moment(`${year}-${month}`).startOf('month').format(DATE_TIME_FORMAT);
  const endDate = moment(`${year}-${month}`).endOf('month').format(DATE_TIME_FORMAT);

  const params = { startDate, endDate, timeZoneOffset: moment().utcOffset() };

  if (siteID) {
    params.siteID = siteID;
  }

  try {
    const { payload: responsePayload, warnings } = yield axios.get(`${STATISTICS}/tasks/schedule-statistics`, {
      params,
    });
    yield put(biTasksScheduleStatisticsSuccess(responsePayload, { warnings }));
  } catch (err) {
    yield put(biTasksScheduleStatisticsFailure(err));
  }
}

function* biTasksGroupByEmployeeSaga({ payload: { month, year, siteID } }) {
  const startDate = moment(`${year}-${month}`).startOf('month').format(DATE_TIME_FORMAT);
  const endDate = moment(`${year}-${month}`).endOf('month').format(DATE_TIME_FORMAT);

  const params = { startDate, endDate, timeZoneOffset: moment().utcOffset() };

  if (siteID) {
    params.siteID = siteID;
  }

  try {
    const { payload: responsePayload, warnings } = yield axios.get(`${STATISTICS}/tasks/group-by-employee`, { params });
    yield put(biTasksByEmployeeSuccess(responsePayload, { warnings }));
  } catch (err) {
    yield put(biTasksByEmployeeFailure(err));
  }
}

function* biTaskLogSaga({ payload: { month, year, siteID, taskID, employeeID } }) {
  const startDate = moment(`${year}-${month}`).startOf('month').format(DATE_TIME_FORMAT);
  const endDate = moment(`${year}-${month}`).endOf('month').format(DATE_TIME_FORMAT);

  const params = { startDate, endDate, timeZoneOffset: moment().utcOffset() };

  if (siteID) {
    params.siteID = siteID;
  }

  if (taskID) {
    params.taskID = taskID;
  }

  if (employeeID) {
    params.employeeID = employeeID;
  }

  try {
    const { payload: responsePayload, warnings } = yield axios.get(`${STATISTICS}/tasks/task-log`, { params });
    yield put(biTaskLogSuccess(responsePayload, { warnings }));
  } catch (err) {
    yield put(biTaskLogFailure(err));
  }
}

export default function* () {
  yield all([
    takeLatest(biTasksBySiteAndDateRequest, biTasksBySiteAndDateSaga),
    takeLatest(biTasksByDateRequest, biTasksByDateSaga),
    takeLatest(biTasksBySiteRequest, biTasksBySiteSaga),
    takeLatest(biTasksScheduleStatisticsRequest, biTasksScheduleStatisticsSaga),
    takeLatest(biTasksByEmployeeRequest, biTasksGroupByEmployeeSaga),
    takeLatest(biTaskLogRequest, biTaskLogSaga),
  ]);
}
